/**
 * Rewards Catalog tab — list, add, edit, and manage reward items.
 *
 * Reuses AddRewardDialog from Epic 02 for creating new rewards.
 * Uses EditRewardDialog for editing.
 *
 * Plan 177, Epic 07 — Admin Management & Analytics
 */

import { useState } from "react";
import { Gift, Plus, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  useAdminRewards,
  useUpdateReward,
  type RewardCatalogItem,
} from "../api/rewardsAdminHooks";
import { AddRewardDialog } from "./AddRewardDialog";
import { EditRewardDialog } from "./EditRewardDialog";

const REWARD_TYPE_LABELS: Record<string, string> = {
  coupon: "คูปอง",
  free_item: "ของแถม",
  custom: "กำหนดเอง",
};

const STATUS_VARIANTS: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  Active: { label: "ใช้งาน", variant: "default" },
  Paused: { label: "หยุดชั่วคราว", variant: "secondary" },
  Archived: { label: "เก็บถาวร", variant: "outline" },
};

export function CatalogTab() {
  const [addOpen, setAddOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<RewardCatalogItem | null>(null);
  const rewardsQuery = useAdminRewards(true);
  const updateMutation = useUpdateReward();

  const rewards = rewardsQuery.data?.rewards ?? [];

  const handleStatusChange = (reward: RewardCatalogItem, newStatus: string) => {
    updateMutation.mutate(
      { reward_id: reward.name, status: newStatus },
      {
        onSuccess: () =>
          toast.success(
            newStatus === "Paused"
              ? "หยุดรางวัลชั่วคราวสำเร็จ"
              : newStatus === "Active"
                ? "เปิดใช้งานรางวัลสำเร็จ"
                : "เก็บถาวรรางวัลสำเร็จ",
          ),
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      },
    );
  };

  if (rewardsQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">รางวัลทั้งหมด</h3>
          {rewards.length > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium">
              {rewards.length}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAddOpen(true)}
          className="min-h-[36px]"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          เพิ่มรางวัล
        </Button>
      </div>

      {/* Reward list or empty state */}
      {rewards.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Gift className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            เพิ่มรางวัลที่ลูกค้าสามารถแลกด้วยแต้มสะสม
          </p>
          <Button
            variant="outline"
            onClick={() => setAddOpen(true)}
            className="min-h-[44px]"
          >
            <Plus className="mr-2 h-4 w-4" />
            เพิ่มรางวัล
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {rewards.map((reward) => {
            const statusInfo = STATUS_VARIANTS[reward.status] ?? STATUS_VARIANTS.Active;
            return (
              <div
                key={reward.name}
                className="flex items-center gap-3 rounded-lg border p-3"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Gift className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{reward.title}</p>
                    <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0">
                      {statusInfo.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {REWARD_TYPE_LABELS[reward.reward_type] ?? reward.reward_type}
                    {reward.current_redemptions > 0 &&
                      ` · แลกแล้ว ${reward.current_redemptions} ครั้ง`}
                  </p>
                </div>
                <p className="text-sm font-semibold text-amber-600 whitespace-nowrap">
                  {reward.points_cost.toLocaleString()} แต้ม
                </p>

                {/* Actions menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingReward(reward)}>
                      แก้ไข
                    </DropdownMenuItem>
                    {reward.status === "Active" ? (
                      <DropdownMenuItem
                        onClick={() => handleStatusChange(reward, "Paused")}
                      >
                        หยุดชั่วคราว
                      </DropdownMenuItem>
                    ) : reward.status === "Paused" ? (
                      <DropdownMenuItem
                        onClick={() => handleStatusChange(reward, "Active")}
                      >
                        เปิดใช้งาน
                      </DropdownMenuItem>
                    ) : null}
                    {reward.status !== "Archived" && (
                      <DropdownMenuItem
                        onClick={() => handleStatusChange(reward, "Archived")}
                        className="text-destructive"
                      >
                        เก็บถาวร
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AddRewardDialog open={addOpen} onOpenChange={setAddOpen} />

      {editingReward && (
        <EditRewardDialog
          key={editingReward.name}
          reward={editingReward}
          open={!!editingReward}
          onOpenChange={(open) => {
            if (!open) setEditingReward(null);
          }}
        />
      )}
    </div>
  );
}
